import { PAGE_LIBRARIES, PageLibraries, PageLibrary } from './runtime'

/**
 * The libraries a Custom View page can use, which the host puts inside the page as nonce'd inline scripts (the
 * page's CSP allows no others): d3 for charts, three.js for 3D, and Morta's IFC viewer (morta.ifc, with web-ifc).
 * Each is loaded, and sent, only for a page whose code uses it: they're large (d3 280KB, three.js 760KB, the IFC
 * viewer 3.5MB, and web-ifc's 1.6MB wasm, which reaches the page through the bridge only when it opens a model).
 */

const USES_IFC = /\bmorta\.ifc\b/
const USES: Record<PageLibrary, RegExp> = {
  // d3.select, d3.scaleLinear, ...
  d3: /\bd3\s*\./,
  three: new RegExp(`\\bTHREE\\s*\\.|${USES_IFC.source}`),
  ifc: USES_IFC
}

/** The libraries a page's code uses. */
export function pageLibraries(html: string): PageLibrary[] {
  return PAGE_LIBRARIES.filter((name) => USES[name].test(html))
}

// Each a separate chunk, so pages that don't use a library never load it. d3 is imported by its path: its
// package exports only its entry, and a relative path also keeps Vite's dev server from pre-bundling it. three.js
// has no browser build, so it and the IFC viewer are bundled into one script each (scripts/page-script.mjs).
const IMPORTS: Record<PageLibrary, () => Promise<{ default: string }>> = {
  d3: () => import('../../../../../node_modules/d3/dist/d3.min.js?raw'),
  three: () => import('./three.page.ts?page-script'),
  ifc: () => import('./ifc.page.ts?page-script')
}

const sources = new Map<PageLibrary, Promise<string>>()

/** A library's script, fetched once. A failed fetch isn't kept, so the next page that needs it tries again. */
export function loadLibrary(name: PageLibrary): Promise<string> {
  let source = sources.get(name)
  if (!source) {
    source = IMPORTS[name]().then(
      (module) => module.default,
      (error) => {
        sources.delete(name)
        throw error
      }
    )
    sources.set(name, source)
  }
  return source
}

/** The scripts of the libraries a page uses. */
export async function loadLibraries(names: PageLibrary[]): Promise<PageLibraries> {
  const scripts = await Promise.all(names.map(loadLibrary))
  return Object.fromEntries(names.map((name, index) => [name, scripts[index]]))
}

let wasm: Promise<ArrayBuffer> | null = null

/** web-ifc's wasm, which the IFC viewer asks Morta for (morta.ifc.view): the page can't fetch it itself. */
export function loadIfcWasm(): Promise<ArrayBuffer> {
  wasm ??= import('../../../../../node_modules/web-ifc/web-ifc.wasm?url')
    .then((module) => fetch(module.default))
    .then((response) => {
      if (!response.ok) throw new Error(`Morta's IFC reader couldn't load (${response.status}).`)
      return response.arrayBuffer()
    })
    .catch((error) => {
      wasm = null
      throw error
    })
  return wasm
}
